import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Quote,
  Link as LinkIcon,
  Undo2,
  Redo2,
  Strikethrough,
  Eraser,
} from "lucide-react";

const btn =
  "h-8 w-8 inline-flex items-center justify-center rounded-md border border-transparent text-[#1A2E1A] hover:bg-[#F2F5F0] hover:border-[#E5EAE5] transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const btnActive = "bg-[#1A2E1A] text-[#7AC143] hover:bg-[#1A2E1A] hover:text-[#7AC143]";

function Tb({ onClick, active, disabled, label, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={testId}
      className={`${btn} ${active ? btnActive : ""}`}
    >
      {children}
    </button>
  );
}

/**
 * RichTextEditor — TipTap-based WYSIWYG with a compact toolbar.
 *
 * Stores HTML. Pre-existing plain-text values are accepted as-is (TipTap
 * wraps them in a <p>). The Description field on the product form is the
 * primary consumer.
 */
export default function RichTextEditor({ value, onChange, placeholder, testId }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        "data-testid": testId || "rich-text-editor",
        class:
          "tiptap min-h-[160px] max-h-[420px] overflow-y-auto px-3 py-2 outline-none text-[#1A2E1A] leading-relaxed",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Normalise empty editor (TipTap returns "<p></p>") to "" so required-validation works
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Keep external value changes in sync (e.g. when admin opens an existing product)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (next !== current && next !== "<p></p>") {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL du lien", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div
      data-testid={testId ? `${testId}-wrapper` : "rich-text-editor-wrapper"}
      className="border border-[#E5EAE5] rounded-md bg-white focus-within:ring-2 focus-within:ring-[#7AC143] focus-within:border-[#7AC143]"
    >
      <div className="flex flex-wrap items-center gap-1 border-b border-[#E5EAE5] px-2 py-1.5 bg-[#F9FAF6] rounded-t-md">
        <Tb
          label="Gras"
          testId="rte-bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-4 h-4" />
        </Tb>
        <Tb
          label="Italique"
          testId="rte-italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-4 h-4" />
        </Tb>
        <Tb
          label="Barré"
          testId="rte-strike"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="w-4 h-4" />
        </Tb>
        <span className="mx-1 h-5 w-px bg-[#E5EAE5]" aria-hidden="true" />
        <Tb
          label="Titre 2"
          testId="rte-h2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="w-4 h-4" />
        </Tb>
        <Tb
          label="Titre 3"
          testId="rte-h3"
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 className="w-4 h-4" />
        </Tb>
        <span className="mx-1 h-5 w-px bg-[#E5EAE5]" aria-hidden="true" />
        <Tb
          label="Liste à puces"
          testId="rte-bullet"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="w-4 h-4" />
        </Tb>
        <Tb
          label="Liste numérotée"
          testId="rte-ordered"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="w-4 h-4" />
        </Tb>
        <Tb
          label="Citation"
          testId="rte-blockquote"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote className="w-4 h-4" />
        </Tb>
        <span className="mx-1 h-5 w-px bg-[#E5EAE5]" aria-hidden="true" />
        <Tb
          label="Lien"
          testId="rte-link"
          active={editor.isActive("link")}
          onClick={setLink}
        >
          <LinkIcon className="w-4 h-4" />
        </Tb>
        <Tb
          label="Effacer la mise en forme"
          testId="rte-clear"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          <Eraser className="w-4 h-4" />
        </Tb>
        <span className="ml-auto inline-flex items-center gap-1">
          <Tb
            label="Annuler"
            testId="rte-undo"
            disabled={!editor.can().undo()}
            onClick={() => editor.chain().focus().undo().run()}
          >
            <Undo2 className="w-4 h-4" />
          </Tb>
          <Tb
            label="Rétablir"
            testId="rte-redo"
            disabled={!editor.can().redo()}
            onClick={() => editor.chain().focus().redo().run()}
          >
            <Redo2 className="w-4 h-4" />
          </Tb>
        </span>
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}
